import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Créer une instance axios avec configuration de base
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Services d'authentification
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user_type, user_id, role, entreprise_id, entreprise_nom } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('tokenExpiry', JSON.stringify(Date.now() + (user_type === 'EMPLOYE' ? 30 * 60 * 1000 : 15 * 60 * 1000)));
    localStorage.setItem('user', JSON.stringify({
      user_type,
      user_id,
      role,
      entreprise_id,
      entreprise_nom
    }));
    
    return response.data;
  },

  employeeLogin: async (telephone, pin) => {
    const response = await api.post('/auth/employee/login', { telephone, pin });
    const { access_token, user_type, user_id, entreprise_id } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('tokenExpiry', JSON.stringify(Date.now() + 30 * 60 * 1000)); // 30 minutes pour employés
    localStorage.setItem('user', JSON.stringify({
      user_type,
      user_id,
      entreprise_id
    }));
    
    return response.data;
  },

  requestOTP: async (telephone) => {
    const response = await api.post('/auth/employee/request-otp', { telephone });
    return response.data;
  },

  verifyOTP: async (telephone, otp, newPin) => {
    const response = await api.post('/auth/employee/verify-otp', {
      telephone,
      otp,
      new_pin: newPin
    });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', null, {
      params: { email }
    });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  isTokenExpired: () => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return true;
    
    try {
      const expiryTime = JSON.parse(tokenExpiry);
      return Date.now() > expiryTime;
    } catch {
      return true;
    }
  },

  getRemainingTime: () => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return 0;
    
    try {
      const expiryTime = JSON.parse(tokenExpiry);
      const remaining = Math.max(0, expiryTime - Date.now());
      return Math.floor(remaining / 1000); // en secondes
    } catch {
      return 0;
    }
  }
};

// Services admin ALO
export const adminService = {
  getDashboard: async () => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  getEntreprises: async (skip = 0, limit = 100) => {
    const response = await api.get(`/admin/entreprises?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getEntreprisesValidation: async () => {
    const response = await api.get('/admin/entreprises/validation');
    return response.data;
  },

  validerEntreprise: async (entrepriseId) => {
    const response = await api.post(`/admin/entreprises/${entrepriseId}/valider`);
    return response.data;
  },

  rejeterEntreprise: async (entrepriseId, raison) => {
    const response = await api.post(`/admin/entreprises/${entrepriseId}/rejeter`, { raison });
    return response.data;
  },

  suspendreEntreprise: async (entrepriseId, raison) => {
    const response = await api.post(`/admin/entreprises/${entrepriseId}/suspendre`, { raison });
    return response.data;
  },

  reactiverEntreprise: async (entrepriseId) => {
    const response = await api.post(`/admin/entreprises/${entrepriseId}/reactiver`);
    return response.data;
  },

  updateParametresEntreprise: async (entrepriseId, parametres) => {
    const response = await api.put(`/admin/entreprises/${entrepriseId}/parametres`, parametres);
    return response.data;
  },

  getHistoriqueEntreprise: async (entrepriseId) => {
    const response = await api.get(`/admin/entreprises/${entrepriseId}/historique`);
    return response.data;
  },

  // Endpoints financiers
  getFinancialOverview: async () => {
    const response = await api.get('/admin/financier/overview');
    return response.data;
  },

  getFinancialTransactions: async (params = {}) => {
    const response = await api.get('/admin/financier/transactions', { params });
    return response.data;
  },

  getReconciliation: async (periode = 'mois_courant') => {
    const response = await api.get('/admin/financier/reconciliation', { params: { periode } });
    return response.data;
  },

  getCommissions: async () => {
    const response = await api.get('/admin/financier/commissions');
    return response.data;
  },

  // Endpoints conformité
  getAuditLogs: async (params = {}) => {
    const response = await api.get('/admin/conformite/audit_logs', { params });
    return response.data;
  },

  getComplianceAlerts: async () => {
    const response = await api.get('/admin/conformite/alertes');
    return response.data;
  },

  // Endpoints utilisateurs (gestion admins)
  getAdminUsers: async () => {
    const response = await api.get('/admin/utilisateurs');
    return response.data;
  },

  createAdminUser: async (userData) => {
    const response = await api.post('/admin/utilisateurs', userData);
    return response.data;
  },

  updateAdminUser: async (userId, userData) => {
    const response = await api.put(`/admin/utilisateurs/${userId}`, userData);
    return response.data;
  },

  deleteAdminUser: async (userId) => {
    const response = await api.delete(`/admin/utilisateurs/${userId}`);
    return response.data;
  },

  // Historique des actions d'admin
  getAdminActionLogs: async (params = {}) => {
    const response = await api.get('/admin/utilisateurs/logs', { params });
    return response.data;
  },

  getAdminUserLogs: async (userId, params = {}) => {
    const response = await api.get(`/admin/utilisateurs/${userId}/logs`, { params });
    return response.data;
  },

  // Endpoints analytics
  getAnalyticsTrends: async () => {
    const response = await api.get('/admin/analytics/tendances');
    return response.data;
  },

  getTopCompanies: async (critere = 'volume', limit = 10) => {
    const response = await api.get('/admin/analytics/top_entreprises', { params: { critere, limit } });
    return response.data;
  },

  getEmployeeStatistics: async () => {
    const response = await api.get('/admin/analytics/statistiques_employes');
    return response.data;
  },

  // Endpoints employés (Admin ALO)
  getAllEmployees: async (params = {}) => {
    const response = await api.get('/admin/employes', { params });
    return response.data;
  },

  getEmployeeDetails: async (employeId) => {
    const response = await api.get(`/admin/employes/${employeId}`);
    return response.data;
  },

  // Endpoints réinitialisation
  resetEntreprisePassword: async (entrepriseId) => {
    const response = await api.post(`/admin/entreprises/${entrepriseId}/reset-password`);
    return response.data;
  },

  resetEmployePin: async (employeId) => {
    const response = await api.post(`/admin/employes/${employeId}/reset-pin`);
    return response.data;
  },

  // Approvisionnement
  approvisionnerEntreprise: async (entrepriseId, montant, modePaiement, reference = '', notes = '') => {
    const response = await api.post(`/admin/entreprises/${entrepriseId}/approvisionner`, null, {
      params: { montant, mode_paiement: modePaiement, reference, notes }
    });
    return response.data;
  },

  getTransactionsSolde: async (entrepriseId, skip = 0, limit = 50) => {
    const response = await api.get(`/admin/entreprises/${entrepriseId}/transactions-solde`, {
      params: { skip, limit }
    });
    return response.data;
  }
};

// Services entreprise
export const entrepriseService = {
  register: async (entrepriseData) => {
    const response = await api.post('/entreprise/register', entrepriseData);
    return response.data;
  },

  resetEmployePin: async (employeId) => {
    const response = await api.post(`/entreprise/employes/${employeId}/reset-pin`);
    return response.data;
  },

  // Import d'employés
  downloadImportTemplate: async () => {
    const response = await api.get('/entreprise/employes/import/template', {
      responseType: 'blob'
    });
    return response.data;
  },

  importEmployees: async (file, updateDuplicates = true) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(
      `/entreprise/employes/import?update_duplicates=${updateDuplicates}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  getTransactionsSolde: async (skip = 0, limit = 50) => {
    const response = await api.get('/entreprise/transactions-solde', {
      params: { skip, limit }
    });
    return response.data;
  }
};

// Services employé
export const employeService = {
  getProfile: async () => {
    const response = await api.get('/employe/profile');
    return response.data;
  },

  // Avances/Paiements employé
  demanderAvance: async (data) => {
    const response = await api.post('/employe/avances/demander', data);
    return response.data;
  },

  // Nouveau: Paiement direct (remplace demanderAvance)
  effectuerPaiement: async (paiementData) => {
    const response = await api.post('/employe/paiement', paiementData);
    return response.data;
  },

  getAvancesEmploye: async () => {
    const response = await api.get('/employe/avances');
    return response.data;
  }
};

// Services Retrait Instantané (ChapChapPay)
export const withdrawalService = {
  // Récupérer le solde disponible
  getSoldeDisponible: async () => {
    const response = await api.get('/withdrawal/solde');
    return response.data;
  },

  // Initier un retrait
  initiateWithdrawal: async (data) => {
    const response = await api.post('/withdrawal/initiate', data);
    return response.data;
  },

  // Historique des retraits
  getWithdrawalHistory: async (skip = 0, limit = 20) => {
    const response = await api.get('/withdrawal/history', { params: { skip, limit } });
    return response.data;
  }
};

// Service de santé de l'API
export const healthService = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  }
};

export default api;